/**
 * Klaviyo client helpers.
 *
 * The onsite SDK is loaded asynchronously by src/util/includeScripts.js when
 * REACT_APP_KLAVIYO_COMPANY_ID is set. We push through the legacy `_learnq`
 * queue (also initialized in includeScripts.js) so calls made before the
 * async script finishes loading are buffered and drained on SDK ready.
 *
 * All exports here are no-ops when:
 *   - running on the server (no window)
 *   - Klaviyo isn't configured (no _learnq queue was initialized)
 *   - the caller didn't supply enough data to identify the user
 *
 * Wrapped in try/catch so a Klaviyo failure can never break the app.
 */

import { useEffect, useRef } from 'react';

import { getAffiliateRef } from './tapfiliate';

const isBrowser = () => typeof window !== 'undefined';

const klaviyoQueue = () => (isBrowser() ? window._learnq : null);

/**
 * Identify the current Sharetribe user to Klaviyo. Idempotent —
 * Klaviyo merges repeated calls into the same profile by email.
 *
 * If the user arrived via an affiliate ?ref= link (captured to the tapRef
 * cookie), we attach `affiliate_ref` as a profile property. This is purely a
 * stat for us to segment "signups by affiliate" in Klaviyo — affiliates are
 * NOT told about signups and are not paid on them (payouts are purchases only,
 * tracked server-side via Tapfiliate).
 */
export const identifyKlaviyoUser = currentUser => {
  const queue = klaviyoQueue();
  if (!queue) {
    return;
  }

  const email = currentUser?.attributes?.email;
  if (!email) {
    return;
  }

  const profile = currentUser?.attributes?.profile || {};
  const affiliateRef = getAffiliateRef();
  const payload = {
    $email: email,
    ...(profile.firstName ? { $first_name: profile.firstName } : {}),
    ...(profile.lastName ? { $last_name: profile.lastName } : {}),
    ...(currentUser?.id?.uuid ? { sharetribe_user_id: currentUser.id.uuid } : {}),
    ...(affiliateRef ? { affiliate_ref: affiliateRef } : {}),
  };

  try {
    queue.push(['identify', payload]);
  } catch (e) {
    // Never let Klaviyo break the app.
  }
};

/**
 * Build a Klaviyo Viewed Product payload from a Sharetribe listing entity.
 * Sharetribe stores prices as integer subunits; Klaviyo wants a numeric
 * dollar value, so we divide by 100 when present.
 */
const buildViewedProductPayload = listing => {
  const id = listing?.id?.uuid;
  const title = listing?.attributes?.title;
  if (!id || !title) {
    return null;
  }

  const publicData = listing?.attributes?.publicData || {};
  const priceAmount = listing?.attributes?.price?.amount;
  const priceCurrency = listing?.attributes?.price?.currency;
  const price = typeof priceAmount === 'number' ? priceAmount / 100 : undefined;

  const firstImageVariants = listing?.images?.[0]?.attributes?.variants || {};
  const imageUrl =
    firstImageVariants['scaled-large']?.url ||
    firstImageVariants['scaled-medium']?.url ||
    firstImageVariants['scaled-small']?.url ||
    firstImageVariants.default?.url;

  const category = publicData.category || publicData.categoryLevel1;
  const categories = category ? [category] : [];
  const brand = publicData.brand;
  const url = isBrowser() ? `${window.location.origin}${window.location.pathname}` : undefined;

  const payload = {
    ProductName: title,
    ProductID: id,
    SKU: id,
    Categories: categories,
    ...(imageUrl ? { ImageURL: imageUrl } : {}),
    ...(url ? { URL: url } : {}),
    ...(brand ? { Brand: brand } : {}),
    ...(price !== undefined ? { Price: price } : {}),
    ...(priceCurrency ? { Currency: priceCurrency } : {}),
  };

  return payload;
};

/**
 * Build an Added to Cart payload from a Sharetribe listing entity plus the
 * quantity added. Mirrors the Viewed Product schema (top-level $value /
 * AddedItemCategories so Klaviyo segments can filter on them, plus a nested
 * Items[] entry for personalization). Returns null when the listing can't be
 * identified.
 */
const buildAddedToCartPayload = (listing, { quantity = 1 } = {}) => {
  const id = listing?.id?.uuid;
  const title = listing?.attributes?.title;
  if (!id || !title) {
    return null;
  }

  const publicData = listing?.attributes?.publicData || {};
  const priceAmount = listing?.attributes?.price?.amount;
  const priceCurrency = listing?.attributes?.price?.currency;
  const itemPrice = typeof priceAmount === 'number' ? priceAmount / 100 : undefined;

  const firstImageVariants = listing?.images?.[0]?.attributes?.variants || {};
  const imageUrl =
    firstImageVariants['scaled-large']?.url ||
    firstImageVariants['scaled-medium']?.url ||
    firstImageVariants['scaled-small']?.url ||
    firstImageVariants['listing-card']?.url ||
    firstImageVariants.default?.url;

  const category = publicData.category || publicData.categoryLevel1;
  const categories = category ? [category] : [];
  const brand = publicData.brand;
  const url = isBrowser() ? `${window.location.origin}${window.location.pathname}` : undefined;

  const parsedQuantity = Number.parseInt(quantity, 10);
  const safeQuantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
  const rowTotal = itemPrice !== undefined ? itemPrice * safeQuantity : undefined;

  return {
    ...(rowTotal !== undefined ? { $value: rowTotal } : {}),
    AddedItemProductName: title,
    AddedItemProductID: id,
    AddedItemSKU: id,
    AddedItemCategories: categories,
    AddedItemQuantity: safeQuantity,
    ...(imageUrl ? { AddedItemImageURL: imageUrl } : {}),
    ...(url ? { AddedItemURL: url } : {}),
    ...(brand ? { AddedItemBrand: brand } : {}),
    ...(itemPrice !== undefined ? { AddedItemPrice: itemPrice } : {}),
    ...(priceCurrency ? { Currency: priceCurrency } : {}),
    Items: [
      {
        ProductID: id,
        SKU: id,
        ProductName: title,
        Quantity: safeQuantity,
        ...(itemPrice !== undefined ? { ItemPrice: itemPrice } : {}),
        ...(rowTotal !== undefined ? { RowTotal: rowTotal } : {}),
        ...(brand ? { Brand: brand } : {}),
        ...(imageUrl ? { ImageURL: imageUrl } : {}),
        Categories: categories,
      },
    ],
  };
};

/**
 * Push an Added to Cart event for the given listing. No-ops when the Klaviyo
 * SDK isn't configured or the listing can't be identified.
 */
export const trackAddedToCart = (listing, options = {}) => {
  const queue = klaviyoQueue();
  if (!queue) {
    return;
  }

  const payload = buildAddedToCartPayload(listing, options);
  if (!payload) {
    return;
  }

  try {
    queue.push(['track', 'Added to Cart', payload]);
  } catch (e) {
    // Never let Klaviyo break the app.
  }
};

/**
 * Push a Viewed Product event plus a trackViewedItem call (the latter
 * populates the "Recently Viewed Items" array used by Klaviyo email
 * personalization blocks).
 */
export const trackViewedProduct = listing => {
  const queue = klaviyoQueue();
  if (!queue) {
    return;
  }

  const payload = buildViewedProductPayload(listing);
  if (!payload) {
    return;
  }

  try {
    queue.push(['track', 'Viewed Product', payload]);
    queue.push([
      'trackViewedItem',
      {
        Title: payload.ProductName,
        ItemId: payload.ProductID,
        Categories: payload.Categories,
        ...(payload.ImageURL ? { ImageUrl: payload.ImageURL } : {}),
        ...(payload.URL ? { Url: payload.URL } : {}),
        Metadata: {
          ...(payload.Brand ? { Brand: payload.Brand } : {}),
          ...(payload.Price !== undefined ? { Price: payload.Price } : {}),
        },
      },
    ]);
  } catch (e) {
    // Never let Klaviyo break the app.
  }
};

/**
 * React hook that fires Viewed Product exactly once per listing the user
 * lands on. Waits until both the listing id and title are present (the
 * listing data may load incrementally), and uses a ref to avoid double
 * fires from re-renders.
 */
export const useTrackViewedProduct = listing => {
  const lastTrackedIdRef = useRef(null);
  useEffect(() => {
    const id = listing?.id?.uuid;
    const title = listing?.attributes?.title;
    if (id && title && lastTrackedIdRef.current !== id) {
      trackViewedProduct(listing);
      lastTrackedIdRef.current = id;
    }
  }, [listing]);
};

/**
 * Pick the best available image URL from a Sharetribe image entity.
 */
const pickImageUrl = image => {
  const variants = image?.attributes?.variants || {};
  return (
    variants['scaled-large']?.url ||
    variants['scaled-medium']?.url ||
    variants['scaled-small']?.url ||
    variants.default?.url ||
    null
  );
};

/**
 * Turn the primary listing + orderData (Sharetribe's single-listing checkout
 * shape) into a Klaviyo Items[] entry.
 */
const buildSingleListingCheckoutItem = (listing, orderData = {}) => {
  const id = listing?.id?.uuid;
  const title = listing?.attributes?.title;
  if (!id || !title) {
    return null;
  }

  const publicData = listing?.attributes?.publicData || {};
  const priceAmount = listing?.attributes?.price?.amount;
  const itemPrice = typeof priceAmount === 'number' ? priceAmount / 100 : 0;
  const parsedQuantity = Number.parseInt(orderData?.quantity, 10);
  const quantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
  const imageUrl = pickImageUrl(listing?.images?.[0]);
  const category = publicData.category || publicData.categoryLevel1;
  const categories = category ? [category] : [];

  return {
    ProductID: id,
    SKU: id,
    ProductName: title,
    Quantity: quantity,
    ItemPrice: itemPrice,
    RowTotal: itemPrice * quantity,
    ...(publicData.brand ? { Brand: publicData.brand } : {}),
    ...(imageUrl ? { ImageURL: imageUrl } : {}),
    Categories: categories,
    ProductCategories: categories,
  };
};

/**
 * Turn a cart-item record (Tutulist's multi-item cart shape) into a Klaviyo
 * Items[] entry. Cart items carry their own title/price/image snapshot from
 * when the user added the item, so we don't need the full listing entity.
 */
const buildCartCheckoutItem = item => {
  const id = item?.listingId?.uuid || item?.listingId;
  const title = item?.title;
  if (!id || !title) {
    return null;
  }

  const priceAmount = item?.price?.amount;
  const itemPrice = typeof priceAmount === 'number' ? priceAmount / 100 : 0;
  const parsedQuantity = Number.parseInt(item?.stockCount ?? item?.quantity, 10);
  const quantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
  const imageUrl = typeof item?.image === 'string' ? item.image : pickImageUrl(item?.image);

  return {
    ProductID: `${id}`,
    SKU: `${id}`,
    ProductName: title,
    Quantity: quantity,
    ItemPrice: itemPrice,
    RowTotal: itemPrice * quantity,
    ...(imageUrl ? { ImageURL: imageUrl } : {}),
    Categories: [],
    ProductCategories: [],
  };
};

/**
 * Push a Started Checkout event for the current checkout context. Skips
 * inquiry-style or zero-value checkouts so they don't pollute the
 * abandoned-cart flow.
 *
 * Per Klaviyo's integration guide, the payload includes both nested Items
 * (for personalization) and top-level ItemNames / Categories / $value (for
 * segmentation — Klaviyo segments can only filter on top-level properties).
 */
export const trackStartedCheckout = pageData => {
  const queue = klaviyoQueue();
  if (!queue) {
    return;
  }

  const listing = pageData?.listing;
  const primaryId = listing?.id?.uuid;
  if (!primaryId) {
    return;
  }

  const orderData = pageData?.orderData || {};
  const cartItems = Array.isArray(orderData.cartItems) ? orderData.cartItems : null;
  const items =
    cartItems && cartItems.length > 0
      ? cartItems.map(buildCartCheckoutItem).filter(Boolean)
      : [buildSingleListingCheckoutItem(listing, orderData)].filter(Boolean);

  if (items.length === 0) {
    return;
  }

  const totalValue = items.reduce((sum, item) => sum + (item.RowTotal || 0), 0);
  if (!(totalValue > 0)) {
    return;
  }

  const itemNames = items.map(item => item.ProductName).filter(Boolean);
  const categories = Array.from(
    new Set(items.flatMap(item => item.Categories || []).filter(Boolean))
  );
  const currency = listing?.attributes?.price?.currency;
  const checkoutUrl = isBrowser()
    ? `${window.location.origin}${window.location.pathname}`
    : undefined;

  const payload = {
    $event_id: `${primaryId}_${Date.now()}`,
    $value: totalValue,
    ItemNames: itemNames,
    Categories: categories,
    ...(checkoutUrl ? { CheckoutURL: checkoutUrl } : {}),
    ...(currency ? { Currency: currency } : {}),
    Items: items,
  };

  try {
    queue.push(['track', 'Started Checkout', payload]);
  } catch (e) {
    // Never let Klaviyo break the app.
  }
};

/**
 * React hook that fires Started Checkout once per primary-listing checkout
 * context. Waits until the listing id + title are loaded, dedupes via a
 * ref so re-renders within the same checkout don't refire.
 */
export const useTrackStartedCheckout = pageData => {
  const lastTrackedIdRef = useRef(null);
  useEffect(() => {
    const id = pageData?.listing?.id?.uuid;
    const title = pageData?.listing?.attributes?.title;
    if (id && title && lastTrackedIdRef.current !== id) {
      trackStartedCheckout(pageData);
      lastTrackedIdRef.current = id;
    }
  }, [pageData]);
};
